import type {
  PortalChannelBlock,
  PortalCrmBlock,
  PortalCrmSeriesPoint,
  PortalCrmLabels,
  PortalDelta,
} from '../../../../../types/portalReport';
import {
  allSourcesLabel,
  crmCoverageNote,
  crmScopeNote,
} from '../../../../../lib/portalReport/reportCopy';
import { crmSeriesValues, spendByBucket } from '../../../../../lib/portalReport/crm';
import type { CrmMetricKey } from '../../../../../lib/portalReport/crm';
import { computeDelta } from '../../../../reporting/heroCards';
import { Band } from './controls';
import { DeltaLine } from './ReportHero';
import { NestingBar } from './CrmPanel';
import { ClubwiseChannelTable } from './ClubwiseChannelTable';
import { costPerDigitalLeadLabel, digitalLeadsLabel } from '../../../../../lib/portalReport/config';
import { Sparkline } from './Sparkline';
import { DASH, formatReportDate, moneyFull, numberFull, percent } from './format';

// CLIENT PORTAL RESTYLE — the merged CRM hero band.
//
// ⚠️⚠️ ONE BAND, BECAUSE THE TWO IT REPLACES SAID THE SAME THINGS. The black
// hero showed 67 / 47 / 70.1% / $32.80 / $34.20, and the petrol band beneath it
// showed 67 / 49 / 47 / 70.1% / $34.20 again. Merging is not tidying — it
// removes a real repetition, on the part of the page a reader looks at first.
//
// ⚠️⚠️ IT IS THE EXISTING BLACK BAND, EXTENDED DOWNWARD. `.rpt-band--hero` is
// `ink-deep` with NO light-mode override, so the surface is mode-invariant and
// every colour decision below follows from that:
//
//   · THE THREE TOP FIGURES ARE ALL `volt`. Brand rule 4 counts emphasis
//     ELEMENTS, and a row of three sibling figures is ONE emphasis unit rather
//     than three; colouring one of them differently would invent a hierarchy
//     that does not exist between leads, internet leads and sales. Volt is safe
//     here and only here — on cream it is 1.00:1, literally invisible.
//   · THE METRICS ROW IS `var(--rpt-kelly)`, NOT `--rpt-fig`. That token
//     resolves to kelly on dark and drops to `kelly-deep` in LIGHT mode — an
//     adaptation for figures sitting on cream. On this always-dark surface it
//     would make the numbers duller when a reader switches to light (≈4.3:1
//     against kelly's ≈6.1:1) for no benefit. ⚠️ THE RULE: a mode-invariant
//     SURFACE takes a mode-invariant TOKEN. If this band ever moves to a
//     mode-dependent surface, both the volt figures and kelly need revisiting.
//   · NO HARDCODED GREEN. Scott asked for `#15ae4f`, which is two points off
//     kelly on R and G and eight on B — almost certainly eyedropped from a
//     screenshot. A fourth green three points from an existing one is how a
//     palette rots.
//
// ⚠️⚠️ THE 67 AND THE 49 ARE ADJACENT, AND THAT ADJACENCY IS LOAD-BEARING. The
// previous round used the word "Enquiries" so the top number could not be read
// as an ad lead count; Scott wants "Leads", which is his clients' word. What
// makes that safe is now STRUCTURAL rather than lexical — putting the total and
// the online-form subset side by side makes the nesting visible in the LAYOUT,
// which is stronger than a careful noun ever was, and it is what licenses
// dropping the nesting sentence that used to sit under the bar.
//
// ⚠️ THE FALLBACK SENTENCE, AND THE CONDITION THAT WOULD BRING IT BACK, ARE AT
// THE RENDER SITE — beside the JSX that could separate the two figures, which is
// the only place someone breaking the pair would be looking.
//
// ⚠️ "Total leads", not bare "Leads". One extra word, doing the work the noun
// "Enquiries" was doing.

/** One figure on the band: a value, its shape over time, and its delta. */
interface HeroFigure {
  label: string;
  value: string | null;
  metric: CrmMetricKey;
  delta: PortalDelta | null;
  /** One value per bucket, for the sparkline. */
  values: ReadonlyArray<number | null>;
}

export function CrmHeroBand({
  crm,
  combined,
  labels,
  range,
}: {
  crm: PortalCrmBlock;
  /** ⚠️ The ad block the two cost figures divide by. */
  combined: PortalChannelBlock;
  labels: PortalCrmLabels;
  range: { from: string; to: string };
}) {
  const coverage = crmCoverageNote(range, crm.coverage, formatReportDate);
  const scope = crmScopeNote(crm.countedClubs.length, crm.uncountedClubs);
  // ⚠️ A period the CRM does not cover at all suppresses the FIGURES, not just
  // the tone. Rendering zeroes under an explanation still puts zeroes on the
  // page, and a reader takes the number over the sentence every time.
  const unmeasured = crm.coverage.firstDate === null || range.to < crm.coverage.firstDate;

  const spend = spendByBucket(crm.monthlySeries.length ? combined.thirteenMonthSeries : []);
  const spark = (metric: CrmMetricKey): ReadonlyArray<number | null> =>
    crmSeriesValues(crm.monthlySeries as PortalCrmSeriesPoint[], metric, spend);

  const top = topFigures(crm, labels, spark);
  const metrics = metricFigures(crm, labels, spark);

  return (
    <Band variant="hero">
      <span className="rpt-mono">
        {`${formatReportDate(range.from)} – ${formatReportDate(range.to)}`}
      </span>

      {/* ⚠️ BEFORE the figures. Both of these change what the numbers mean, and
          a caveat read after a number has been believed is not a caveat. */}
      {coverage && <p className="rpt-note rpt-muted">{coverage}</p>}
      {scope && <p className="rpt-note rpt-muted">{scope}</p>}
      {/* ⚠️ MONTHLY-GRAIN LEADS (ClubWise): says which whole uploaded months the
          lead figures and the conversion rate cover, and what was left out.
          ABOVE the figures — a caveat read after a number has been believed is
          not a caveat. */}
      {crm.leadBasis && <p className="rpt-note rpt-muted">{crm.leadBasis.note}</p>}

      {!unmeasured && (
        <>
          {/* ⚠️⚠️ NO NESTING SENTENCE, AND THE `NestingBar` BELOW IS WHY (§1a).

              It was here twice, deleted twice, and the second restore was
              argued on the grounds that it had become "the ONLY thing on the
              page saying that internet leads are a SUBSET". That was FALSE:
              thirty-two lines down, `<NestingBar>` decomposes the total into
              the subset and the remainder — "519 INTERNET LEADS · 78.8%"
              beside "140 FROM EVERYWHERE ELSE · 21.2%" — which is a stronger
              statement of subset-hood than any sentence, and cannot be
              skim-read past.

              ⚠️⚠️ A COMMENT ASSERTING "THIS IS NOW THE ONLY X ON THE PAGE" IS
              A CLAIM ABOUT THE RENDERED PAGE, AND THE FILE IT SITS IN CANNOT
              VERIFY IT. If a sentence is ever wanted back, the condition is
              that the `NestingBar` has GONE — not that the layout changed. */}

          {/* ⚠️⚠️ WHY THERE ARE NO DELTAS, WHEN THERE ARE NONE (round 8 §2).

              `previousUnavailableReason` was COMPUTED AND RENDERED NOWHERE — so
              an unusable comparison left every figure on this band showing a
              dash with nothing saying why, and a reader takes absent
              percentages for "no change". The string already existed; this is a
              call site, not new copy.

              ⚠️ ABOVE THE FIGURES, the band's own rule: a caveat read after a
              number has been believed is not a caveat.

              ⚠️ INSIDE `!unmeasured`, not beside the coverage and scope notes
              above it. Those explain why the FIGURES are suppressed; this
              explains why the figures that ARE shown carry no comparison. With
              the period itself unmeasured there is nothing to compare and the
              coverage note has already said everything. */}
          {crm.previousUnavailableReason && (
            <p className="rpt-note rpt-muted">{crm.previousUnavailableReason}</p>
          )}

          <div className="rpt-hero-pair">
            {top.map((f) => (
              <div key={f.label} className="rpt-hero-pair-item">
                <span className="rpt-mono">{f.label}</span>
                {/* ⚠️⚠️ ALL THREE AT THE SAME SIZE, and all three `volt`
                    (Scott). Brand rule 4 counts emphasis ELEMENTS and a row of
                    three sibling figures is ONE unit, so sizing or colouring
                    one differently invents a hierarchy that does not exist
                    between total leads, internet leads and sales. */}
                <p className="rpt-hero-figure rpt-hero-figure--sm">{f.value ?? DASH}</p>
                <FigureShape figure={f} />
              </div>
            ))}
          </div>

          {/* ⚠️ CLUBWISE: a month part-way through. Under the top row it qualifies. */}
          {crm.leadBasis?.monthToDateNote && (
            <p className="rpt-note rpt-muted">{crm.leadBasis.monthToDateNote}</p>
          )}

          <div className="rpt-stat-grid rpt-stat-grid--4">
            {metrics.map((f) => (
              <div key={f.label} className="rpt-stat">
                <span className="rpt-stat-figure">{f.value ?? DASH}</span>
                <span className="rpt-mono rpt-stat-label">{f.label}</span>
                <FigureShape figure={f} />
              </div>
            ))}
          </div>

          {/* ⚠️⚠️ NO CAVEAT LINE HERE, AND NO COMPARISON-BASIS LINE (§1b/§1c).

              The club-level caveat did not simply go: it MOVED INTO THE THREE
              LABELS above, as `· All sources`. A label travels with its figure
              into a screenshot and into a renewal conversation; a footnote
              under the row does not, and is not read. See `allSourcesLabel`
              for why `COST PER SALE` cannot ship unqualified.

              ⚠️ The basis line went outright: §5b already states the
              comparison window in the report HEADER, beside the period it
              qualifies, so the page was saying it twice. */}

          {/* ⚠️ CLUBWISE: the channel-type breakdown REPLACES the two-segment bar. */}
          {crm.clubwise ? (
            <ClubwiseChannelTable block={crm.clubwise} />
          ) : (
            crm.totals.leads !== null &&
            crm.totals.digitalLeads !== null && (
              <NestingBar leads={crm.totals.leads} digitalLeads={crm.totals.digitalLeads} />
            )
          )}
        </>
      )}
    </Band>
  );
}

/**
 * The sparkline and the delta, in that order.
 *
 * ⚠️ A metric with no honest year-on-year comparison renders NOTHING here rather
 * than a dash or a reason. On this band three of the seven figures legitimately
 * have no delta — the cost ones need last year's ad spend, which most clubs do
 * not have — and seven tiles where three say "no comparison" reads as three
 * broken tiles. The basis line under the row is what explains the absence, once.
 */
function FigureShape({ figure }: { figure: HeroFigure }) {
  return (
    <>
      <Sparkline values={figure.values} label={`${figure.label} over the last 13 months`} />
      {figure.delta && (
        <DeltaLine
          stat={{
            label: figure.label,
            value: figure.value,
            delta: figure.delta,
            metric: figure.metric,
            unavailable: null,
          }}
        />
      )}
    </>
  );
}

/**
 * Total leads · internet leads · sales.
 *
 * ⚠️ ALL THREE CAN COMPARE YEAR-ON-YEAR where the CRM covers last year — they
 * are CRM counts and nothing else. It is the cost figures below that cannot.
 */
function topFigures(
  crm: PortalCrmBlock,
  labels: PortalCrmLabels,
  spark: (m: CrmMetricKey) => ReadonlyArray<number | null>,
): HeroFigure[] {
  const prev = crm.previous;
  const d = (current: number | null, previous: number | null): PortalDelta | null =>
    prev === null || current === null || previous === null ? null : computeDelta(current, previous);
  return [
    {
      label: labels.crmTotalLeads,
      // ⚠️ NULL is a dash, never 0 (monthly-grain source with no whole uploaded month).
      value: crm.totals.leads === null ? null : numberFull(crm.totals.leads),
      metric: 'crmLeads',
      delta: d(crm.totals.leads, prev?.leads ?? null),
      values: spark('crmLeads'),
    },
    {
      label: digitalLeadsLabel(labels),
      value: crm.totals.digitalLeads === null ? null : numberFull(crm.totals.digitalLeads),
      metric: 'crmDigitalLeads',
      delta: d(crm.totals.digitalLeads, prev?.digitalLeads ?? null),
      values: spark('crmDigitalLeads'),
    },
    // ⚠️ CLUBWISE ONLY: staff generated leads, with its own monthly sparkline and a
    // delta against the previous equivalent whole months.
    ...(crm.clubwise
      ? [
          {
            label: 'Staff generated leads',
            value: crm.clubwise.staffLeads === null ? null : numberFull(crm.clubwise.staffLeads),
            metric: 'crmStaffLeads' as const,
            delta:
              prev === null || crm.clubwise.staffLeads === null || crm.clubwise.previous === null
                ? null
                : computeDelta(crm.clubwise.staffLeads, crm.clubwise.previous.staffLeads),
            values: spark('crmStaffLeads'),
          },
        ]
      : []),
    {
      label: labels.sales,
      value: numberFull(crm.totals.sales),
      metric: 'crmSales',
      delta: d(crm.totals.sales, prev?.sales ?? null),
      values: spark('crmSales'),
    },
  ];
}

/**
 * Conversion rate · cost per internet lead · cost per sale · total media spend.
 *
 * ⚠️⚠️ THE THREE MONEY FIGURES CARRY NO DELTA UNLESS LAST YEAR'S SPEND EXISTS,
 * and on most clubs it does not — ad history typically starts long after the
 * CRM's does. `previousCostPerSale` and friends are null unless BOTH halves are
 * measured, which is decided in `buildCrmBlock`; this only renders what it is
 * given. ⚠️ Do not add a previous-period fallback for them: two bases on one
 * band is what the single basis line makes impossible to explain.
 *
 * ⚠️ TOTAL MEDIA SPEND belongs on this row because it is the NUMERATOR of the
 * two cost figures beside it, which makes those two auditable rather than
 * assertions.
 */
/**
 * ⚠️⚠️ THREE OF THE FOUR CARRY `· ALL SOURCES` (§1c, 2026-09-04) — the
 * disclosure that used to be a nineteen-word footnote under the row.
 *
 * Conversion rate and both cost figures are the CLUB'S OWN totals over ALL
 * media spend, from every source: walk-ins, referrals and members who found
 * them without an ad. The ad-only figures are UNKNOWABLE (the CRM records no
 * channel on a sale row — measured, 61,350 of 61,350 blank) and HIGHER. See
 * `allSourcesLabel` for why `COST PER SALE` cannot ship unqualified.
 *
 * ⚠️⚠️ `TOTAL MEDIA SPEND` TAKES NO SUFFIX, and that is not an oversight: it is
 * the NUMERATOR of the two cost figures beside it and genuinely IS ads only.
 * Suffixing it would disclaim a figure that needs no disclaimer and dilute the
 * three that do.
 *
 * ⚠️ APPLIED AT THE RENDER, not baked into `PortalCrmLabels`. That registry is
 * a BRAND'S vocabulary, so a brand that renames "conversion rate" must still
 * get the suffix — and the suffix is a fact about the DATA rather than about
 * the brand's preferred word for it.
 */
function metricFigures(
  crm: PortalCrmBlock,
  labels: PortalCrmLabels,
  spark: (m: CrmMetricKey) => ReadonlyArray<number | null>,
): HeroFigure[] {
  const prev = crm.previous;
  return [
    {
      label: allSourcesLabel(labels.conversionRate),
      // ⚠️ NULL is a dash, never 0%. No enquiries means nothing converted OR
      // failed to; a 0% reads as a club that turned everyone away.
      value: crm.totals.conversionRate === null ? null : percent(crm.totals.conversionRate),
      metric: 'crmCloseRate',
      delta:
        prev === null ||
        crm.totals.conversionRate === null ||
        prev.conversionRate === null
          ? null
          : computeDelta(crm.totals.conversionRate, prev.conversionRate),
      values: spark('crmCloseRate'),
    },
    {
      label: allSourcesLabel(costPerDigitalLeadLabel(labels)),
      value: perCurrencyMoney(
        crm.costPerDigitalLead.map((c) => ({
          currency: c.currency,
          value: c.costPerDigitalLead,
        })),
      ),
      metric: 'crmCostPerDigitalLead',
      delta: moneyDelta(
        crm.costPerDigitalLead.map((c) => ({ currency: c.currency, value: c.costPerDigitalLead })),
        crm.previousCostPerDigitalLead?.map((c) => ({
          currency: c.currency,
          value: c.costPerDigitalLead,
        })) ?? null,
      ),
      values: spark('crmCostPerDigitalLead'),
    },
    {
      label: allSourcesLabel(labels.costPerSale),
      value: perCurrencyMoney(
        crm.costPerSale.map((c) => ({ currency: c.currency, value: c.costPerSale })),
      ),
      metric: 'crmCostPerSale',
      delta: moneyDelta(
        crm.costPerSale.map((c) => ({ currency: c.currency, value: c.costPerSale })),
        crm.previousCostPerSale?.map((c) => ({ currency: c.currency, value: c.costPerSale })) ??
          null,
      ),
      values: spark('crmCostPerSale'),
    },
    {
      label: 'Total media spend',
      value: perCurrencyMoney(
        crm.spendByCurrency.map((c) => ({ currency: c.currency, value: c.spend })),
      ),
      // ⚠️ Spend's tone is NEUTRAL, so this reads as movement rather than as
      // good or bad news — the same rule the ad hero already follows.
      metric: 'crmCostPerSale',
      delta: moneyDelta(
        crm.spendByCurrency.map((c) => ({ currency: c.currency, value: c.spend })),
        crm.previousSpendByCurrency?.map((c) => ({ currency: c.currency, value: c.spend })) ?? null,
      ),
      // ⚠️ NO SPARKLINE — spend is not a CRM metric and has no CRM series. The
      // ad charts below plot it; drawing a fourth shape here from a different
      // source would invite reading the four as one measurement.
      values: [],
    },
  ];
}

type Money = { currency: string; value: number | null };

/**
 * ⚠️ PER CURRENCY, joined. Returns null when NOTHING is measurable, so the
 * caller renders an em dash rather than an empty string that reads as a styled
 * zero.
 */
function perCurrencyMoney(entries: readonly Money[]): string | null {
  const parts = entries
    .map((e) => (e.value === null ? null : moneyFull(e.value, e.currency)))
    .filter((p): p is string => p !== null);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * A money delta, or null.
 *
 * ⚠️⚠️ A MIXED-CURRENCY SCOPE GETS NO DELTA AT ALL. There is no single figure to
 * take a percentage of, and blending AUD into NZD to produce one would be a
 * number that exists in no currency — the rule the staff dashboards' money
 * deltas already follow, and the reason this report never blends.
 *
 * ⚠️ AND THE TWO PERIODS MUST BE IN THE SAME CURRENCY, or the percentage
 * compares two different units under one label.
 */
function moneyDelta(current: readonly Money[], previous: readonly Money[] | null): PortalDelta | null {
  if (previous === null || current.length !== 1 || previous.length !== 1) return null;
  const now = current[0] as Money;
  const then = previous[0] as Money;
  if (now.currency !== then.currency) return null;
  if (now.value === null || then.value === null) return null;
  return computeDelta(now.value, then.value);
}
